import { useEffect, useRef } from 'react'
import * as signalR from '@microsoft/signalr'
import { useAuthStore } from '../store/authStore'
import type { EdgeTagValue, MachineLog } from '../types'

export function useServerStatusHub(
  onStatusUpdate: (serverId: string, status: unknown) => void,
  onOnlineChanged: (serverId: string, isOnline: boolean) => void,
  companyId?: string | null,
  onLastDataAt?: (serverId: string, lastDataAt: string) => void,
  isAdmin?: boolean
) {
  const connectionRef = useRef<signalR.HubConnection | null>(null)
  const hasToken = useAuthStore(s => !!s.accessToken)

  useEffect(() => {
    if (!hasToken || (!isAdmin && !companyId)) return

    const connection = new signalR.HubConnectionBuilder()
      .withUrl('/hubs/server-status', {
        // (재)접속 시점의 최신 토큰을 읽는다 — 선제 갱신으로 토큰이 바뀌어도 연결을 다시 만들지 않는다
        accessTokenFactory: () => useAuthStore.getState().accessToken ?? '',
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build()

    connection.on('ReceiveServerStatus', onStatusUpdate)
    connection.on('ServerOnlineChanged', onOnlineChanged)
    if (onLastDataAt) connection.on('ServerLastDataAt', onLastDataAt)

    connection.start().then(() => {
      if (isAdmin) connection.invoke('JoinAdminGroup')
      else connection.invoke('JoinCompanyGroup', companyId)
    })

    connectionRef.current = connection

    return () => {
      connection.stop()
    }
  }, [hasToken, companyId, isAdmin])

  const joinServer = (serverId: string) =>
    connectionRef.current?.invoke('JoinServerGroup', serverId)

  const leaveServer = (serverId: string) =>
    connectionRef.current?.invoke('LeaveServerGroup', serverId)

  return { joinServer, leaveServer }
}

// Admin 전용: 원격 명령 발행/결과 + 모니터링 상태 변경 구독 (admins 그룹)
export function useAdminServerEvents(
  enabled: boolean,
  onCommandCompleted: (serverId: string, commandId: string, success: boolean, message: string) => void,
  onMonitoringStateChanged: (serverId: string, state: string) => void,
  onCommandIssued?: (serverId: string, commandId: string, type: string) => void,
) {
  const hasToken = useAuthStore(s => !!s.accessToken)
  const completedRef = useRef(onCommandCompleted)
  completedRef.current = onCommandCompleted
  const stateRef = useRef(onMonitoringStateChanged)
  stateRef.current = onMonitoringStateChanged
  const issuedRef = useRef(onCommandIssued)
  issuedRef.current = onCommandIssued

  useEffect(() => {
    if (!hasToken || !enabled) return

    const connection = new signalR.HubConnectionBuilder()
      .withUrl('/hubs/server-status', {
        // (재)접속 시점의 최신 토큰을 읽는다 — 선제 갱신으로 토큰이 바뀌어도 연결을 다시 만들지 않는다
        accessTokenFactory: () => useAuthStore.getState().accessToken ?? '',
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build()

    connection.on('ServerCommandCompleted', (serverId: string, commandId: string, success: boolean, message: string) =>
      completedRef.current(serverId, commandId, success, message))
    connection.on('MonitoringStateChanged', (serverId: string, state: string) =>
      stateRef.current(serverId, state))
    connection.on('ServerCommandIssued', (serverId: string, commandId: string, type: string) =>
      issuedRef.current?.(serverId, commandId, type))

    const joinGroup = () => connection.invoke('JoinAdminGroup')
    connection.start().then(joinGroup)
    connection.onreconnected(joinGroup)

    return () => {
      connection.stop()
    }
  }, [hasToken, enabled])
}

// 서버 상세 [실시간] 탭 전용: server:{id} 그룹 조인 + ReceiveMachineLogs 구독
export function useServerLogsHub(
  serverId: string | undefined,
  onLogs: (logs: MachineLog[]) => void
) {
  const hasToken = useAuthStore(s => !!s.accessToken)
  const onLogsRef = useRef(onLogs)
  onLogsRef.current = onLogs

  useEffect(() => {
    if (!hasToken || !serverId) return

    const connection = new signalR.HubConnectionBuilder()
      .withUrl('/hubs/server-status', {
        // (재)접속 시점의 최신 토큰을 읽는다 — 선제 갱신으로 토큰이 바뀌어도 연결을 다시 만들지 않는다
        accessTokenFactory: () => useAuthStore.getState().accessToken ?? '',
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build()

    connection.on('ReceiveMachineLogs', (_serverId: string, logs: MachineLog[]) => {
      onLogsRef.current(logs)
    })

    const joinGroup = () => connection.invoke('JoinServerGroup', serverId)

    connection.start().then(joinGroup)
    // 재연결 후에도 그룹 다시 조인
    connection.onreconnected(joinGroup)

    return () => {
      connection.stop()
    }
  }, [hasToken, serverId])
}

// 서버 상세 [디바이스] 탭 전용: tags:{id} 그룹 조인 + ReceiveTagValues 구독.
// 첫 시청자가 조인하면 API 가 엣지에 스트림을 켜고, 연결을 닫으면(탭 이탈) 마지막 시청자일 때 끈다.
export function useTagStreamHub(
  serverId: string | undefined,
  onValues: (values: EdgeTagValue[]) => void
) {
  const hasToken = useAuthStore(s => !!s.accessToken)
  const onValuesRef = useRef(onValues)
  useEffect(() => { onValuesRef.current = onValues }, [onValues])

  useEffect(() => {
    if (!hasToken || !serverId) return

    const connection = new signalR.HubConnectionBuilder()
      .withUrl('/hubs/server-status', {
        // (재)접속 시점의 최신 토큰을 읽는다 — 선제 갱신으로 토큰이 바뀌어도 연결을 다시 만들지 않는다
        accessTokenFactory: () => useAuthStore.getState().accessToken ?? '',
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build()

    connection.on('ReceiveTagValues', (_serverId: string, values: EdgeTagValue[]) => {
      onValuesRef.current(values)
    })

    const join = () => connection.invoke('JoinTagStream', serverId)

    connection.start().then(join)
    // 재연결은 새 연결이라 API 쪽 시청 등록도 새로 해야 한다
    connection.onreconnected(join)

    return () => {
      connection.stop()
    }
  }, [hasToken, serverId])
}
